import { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, Loader2, RefreshCw, AlertTriangle, ImageOff, RotateCw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { OPS } from "@/constants/testIds";
import { compressImage, PHOTO_EVENT, photoRecordId, queuePhoto, resolvePhotoState, sendPhoto } from "@/lib/coPhoto";
import { PHOTO_STATUS_KEY } from "./coUtils";

const C = OPS.co;

// the field says «تعذّر رفع الصورة» where a record says «بدون صورة»
const LABEL = { ...PHOTO_STATUS_KEY, failed: "co_photo_upload_failed" };
const ICON = { uploading: Loader2, checking: Loader2, stored: CheckCircle2, pending: Loader2, failed: ImageOff,
    lost: ImageOff, missing: ImageOff, reserve_failed: AlertTriangle, unchecked: AlertTriangle };
const DANGER = "text-[color:var(--danger)]";
const TONE = { stored: "text-[color:var(--success,#15803d)]", failed: DANGER, lost: DANGER, reserve_failed: DANGER,
    unchecked: DANGER };
const SPIN = new Set(["uploading", "pending", "checking"]);

/** The field value for a key this form did not just upload (a draft, an edit):
 *  asked of this device and the server, never assumed «stored». */
export async function photoValueFor(key) {
    const s = await resolvePhotoState(key);
    if (s === "gone") return { key: null, state: "lost", preview: null };   // reaped or not ours: retake
    return { key, state: s === "unknown" ? "unchecked" : s, preview: null };
}

/**
 * Camera first (message 16 §7). Taking the picture = 3 taps (open, shutter,
 * confirm — the OS camera UI). The moment the file arrives we compress it,
 * reserve a key, park the blob in IndexedDB and start the PUT in the
 * background; the form never waits for the bytes.
 *
 * Incident 2026-10-21: «تم حفظ الصورة» only after the server took the bytes.
 * The compressed blob stays in memory until then; when IndexedDB refuses it
 * (an iPhone private tab, a full disk) it is uploaded from memory and the
 * field says the screen must stay open; a failed upload offers «إعادة الرفع»
 * and «إعادة التصوير», a key whose bytes are nowhere is `lost` (retake).
 *
 * `value` = { key, state, preview, parked, reason } with state in
 * none | uploading | stored | failed | pending | reserve_failed | lost |
 * checking | unchecked | missing. `parked: false` = the bytes live only in
 * this tab; `reason: "rejected"` = the server refused them (retake only).
 * onChange(object) = a new photo; onChange(fn) = the same photo moving on.
 */
export function PhotoField({ branchId, value, onChange, disabled, onCaptured }) {
    const { t } = useI18n();
    // the parked row names its owner: on a shared till only that user retries it (coPhoto.js)
    const userId = useAuth()?.me?.user?.id || null;
    const inputRef = useRef(null);
    const [pendingBlob, setPendingBlob] = useState(null);
    // the compressed photo of the current key, kept until the server confirms it
    const held = useRef(null);
    const keyNow = useRef(value?.key);
    keyNow.current = value?.key;
    const change = useRef(onChange);
    change.current = onChange;
    const [broken, setBroken] = useState(false);

    useEffect(() => () => { if (value?.preview) URL.revokeObjectURL(value.preview); }, [value?.preview]);
    useEffect(() => { setBroken(false); }, [value?.preview]);

    // the same key may be sent from elsewhere in this tab (a parked retry from «إدخالاتي», an
    // upload started before this form was re-opened): follow how it ends
    useEffect(() => {
        const key = value?.key;
        if (!key) return undefined;
        const on = (e) => {
            const d = e?.detail || {};
            if (d.photo_key !== key || (d.state !== "stored" && d.state !== "failed")) return;
            change.current((cur) => (cur?.key === key && cur.state !== d.state ? { ...cur, state: d.state } : cur));
        };
        window.addEventListener(PHOTO_EVENT, on);
        return () => window.removeEventListener(PHOTO_EVENT, on);
    }, [value?.key]);

    const send = (key, blob, parked) => {
        sendPhoto(key, blob, { parked }).then((res) => {
            if (res === "rejected") toast(t("co_photo_rejected_notice"), { id: `co-photo-${key}` });
            if (res === "stored" && held.current?.key === key) held.current = null;
            const next = { stored: "stored", pending: "pending", rejected: "failed", failed: "failed", lost: "lost" }[res]
                || "failed";
            // the record was saved meanwhile and its photo never arrived: say so once
            if (keyNow.current !== key && next === "failed" && res !== "rejected" && photoRecordId(key)) {
                toast.warning(t("co_photo_after_save_failed"), { id: `co-photo-${key}` });
            }
            onChange((cur) => (cur?.key === key
                ? { ...cur, state: next, reason: res === "rejected" ? "rejected" : null } : cur));
        });
    };

    const reserveAndUpload = async (blob, preview) => {
        let key;
        try {
            const r = await api.post("/co/photos/init", { branch_id: branchId }, { timeout: 15000 });
            key = r.data.photo_key;
        } catch {
            setPendingBlob(blob);
            onChange({ key: null, state: "reserve_failed", preview, parked: null });
            return;
        }
        const parked = await queuePhoto({ photo_key: key, blob, record_id: null, created_by: userId });
        held.current = { key, blob };
        onChange({ key, state: "uploading", preview, parked });
        setPendingBlob(null);
        onCaptured && onCaptured();
        send(key, blob, parked);
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !branchId) return;
        let blob;
        try { blob = await compressImage(file); } catch { blob = file; }
        const preview = URL.createObjectURL(blob);
        held.current = null;
        onChange({ key: null, state: "uploading", preview, parked: null });
        await reserveAndUpload(blob, preview);
    };

    const retryReserve = () => { if (pendingBlob) reserveAndUpload(pendingBlob, value?.preview); };

    const state = value?.state || "none";
    const key = value?.key;
    // the bytes are still here and the server did not refuse them: send them again (from memory —
    // a parked copy is dropped once the PUTs give up)
    const canResend = state === "failed" && !!key && held.current?.key === key && value?.reason !== "rejected";
    const resend = () => {
        if (!canResend) return;
        onChange((cur) => (cur?.key === key ? { ...cur, state: "uploading", parked: false } : cur));
        send(key, held.current.blob, false);
    };
    const recheck = async () => {
        if (!key) return;
        onChange((cur) => (cur?.key === key ? { ...cur, state: "checking" } : cur));
        const v = await photoValueFor(key);
        onChange((cur) => (cur?.key === key ? { ...cur, ...v, preview: cur.preview } : cur));
    };
    const retake = () => inputRef.current?.click();
    const onStatus = { reserve_failed: retryReserve, failed: canResend ? resend : retake, lost: retake,
        unchecked: recheck }[state];

    // one line under the status: why, and what to do
    let warn = null;
    if (state === "uploading" && value?.parked === false) warn = ["not_kept", "co_photo_not_kept"];
    else if (state === "failed") warn = ["failed", canResend ? "co_photo_upload_failed_hint" : "co_photo_rejected_notice"];
    else if (state === "lost") warn = ["lost", "co_photo_lost_hint"];

    const Icon = ICON[state];
    const tone = TONE[state] || "text-[color:var(--ink-muted)]";

    return (
        <div className="space-y-2">
            <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
                   data-testid={C.photo.input} onChange={onFile} disabled={disabled || !branchId} />
            {state === "none" ? (
                <button type="button" data-testid={C.photo.button} disabled={disabled || !branchId}
                        onClick={() => inputRef.current?.click()}
                        className="flex h-28 w-full flex-col items-center justify-center gap-2 rounded-[var(--radius-lg)] border-2 border-dashed border-[color:var(--brand-orange)]/50 bg-[color:var(--brand-orange-soft)] text-[color:var(--brand-brown)] disabled:opacity-50">
                    <Camera className="h-7 w-7" />
                    <span className="text-sm font-bold">{t("co_photo_take")}</span>
                </button>
            ) : (
                <div className="flex items-start gap-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-2">
                    {value?.preview && !broken ? (
                        <img src={value.preview} alt="" data-testid={C.photo.preview} onError={() => setBroken(true)}
                             className="h-16 w-16 shrink-0 rounded-[var(--radius-sm)] object-cover" />
                    ) : (
                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[color:var(--bg-surface-2)]">
                            <Camera className="h-6 w-6 text-[color:var(--ink-faint)]" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1 space-y-1">
                        {/* Incident 2026-10-24: a translated page (Chrome) swaps bare text nodes for its own
                            <font>; React then inserts the next icon before a node that is gone (NotFoundError →
                            the error page). Every label sits in its own element; the status is never translated. */}
                        <button type="button" data-testid={C.photo.status} data-state={state} translate="no"
                                data-parked={value?.parked === false ? "false" : (value?.parked ? "true" : undefined)}
                                onClick={onStatus} disabled={disabled && !!onStatus}
                                className={`flex items-center gap-1.5 text-start text-xs font-semibold ${tone}`}>
                            {Icon ? <Icon className={`h-4 w-4 shrink-0 ${SPIN.has(state) ? "animate-spin" : ""}`} /> : null}
                            <span>{t(LABEL[state] || "co_photo_pending")}</span>
                        </button>
                        {warn ? (
                            <p data-testid={C.photo.warning} data-kind={warn[0]} role={warn[0] === "not_kept" ? "status" : "alert"}
                               className={`text-[11px] font-semibold leading-snug ${warn[0] === "not_kept" ? "text-[color:var(--warning-soft-fg)]" : DANGER}`}>
                                {t(warn[1])}
                            </p>
                        ) : null}
                        {canResend ? (
                            <button type="button" data-testid={C.photo.retry} onClick={resend} disabled={disabled}
                                    className="inline-flex h-8 items-center gap-1 rounded-full border border-[color:var(--danger)]/40 px-3 text-xs font-semibold text-[color:var(--danger)]">
                                <RotateCw className="h-3.5 w-3.5" />
                                <span>{t("co_photo_retry_upload")}</span>
                            </button>
                        ) : null}
                    </div>
                    <button type="button" data-testid={C.photo.retake} disabled={disabled} onClick={retake}
                            className="inline-flex h-9 shrink-0 items-center gap-1 rounded-full border border-[color:var(--border-soft)] px-3 text-xs font-semibold text-[color:var(--ink)]">
                        <RefreshCw className="h-3.5 w-3.5" />
                        <span>{t("co_photo_retake")}</span>
                    </button>
                </div>
            )}
        </div>
    );
}
